'use client';

import Image from 'next/image';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../core/Tracking/Amplitude/Amplitude';
import { BrandHandMarkTwoLinesVariant3 } from '../../../../design-system/components/illustration/BrandHandMarkTwoLinesVariant3';
import { EvaneosSustainable } from '../../../../design-system/components/illustration/EvaneosSustainable';
import { NaturalScrollCarousel } from '../../../../design-system/components/natural-scroll-carousel/NaturalScrollCarousel';
import { Typography } from '../../../../design-system/components/typography/Typography';
import { imageLoaderForId } from '../../../../domains/Layout/Image/URL/ImageURL';
import { HomePageDocument } from '../../../../resources/homePageDocument/domain/types/homePageDocument';
import { useTrackBlockViewed } from '../../../../shared/hooks/useTrackBlockViewed';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';

import style from './BrandCommunicationSection.module.css';
import { BrandCommunicationCard } from './BrandNewsCard/BrandNewsCard';

type BrandCommunicationSectionProps = {
    sustainable: PickDomain<
        HomePageDocument['sustainable'],
        'subtitle' | 'description',
        {
            link: HomePageDocument['sustainable']['link'] | null;
        }
    > | null;
    brandNews: HomePageDocument['brandNews'];
};

export function BrandCommunicationSection({
    sustainable,
    brandNews: brandCommunications,
}: BrandCommunicationSectionProps) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const sustainableBlockRef = useRef<HTMLDivElement>(null);
    const newsBlockRef = useRef<HTMLDivElement>(null);

    useTrackBlockViewed(sustainableBlockRef, 'sustainability');
    useTrackBlockViewed(newsBlockRef, 'brand');

    return (
        <section className={style.section}>
            <Image
                fill
                alt=""
                src="1824955"
                sizes="100vw"
                loader={imageLoaderForId({
                    aspectRatio: 8 / 10,
                })}
                className={style.backgroundImage}
            />
            <div className="layout-with-centered-content">
                <div className="main-container">
                    <div className={style.container}>
                        {sustainable ? (
                            <div ref={sustainableBlockRef} className={style.sustainableBlock}>
                                <div className={style.sustainableHeader}>
                                    <h2 className={style.sustainableTitle}>
                                        {formatMessage({
                                            id: 'page.home.brandSection.sustainableTitle',
                                        })}
                                    </h2>
                                    <EvaneosSustainable className={style.sustainableLogo} />
                                </div>

                                <div className={style.sustainableContent}>
                                    <Typography
                                        as="div"
                                        className={style.sustainableSubtitle}
                                        scale="secondary-xs-bold"
                                    >
                                        {sustainable.subtitle}
                                    </Typography>

                                    <Typography as="p" scale="primary-body-regular">
                                        {sustainable.description}
                                    </Typography>

                                    {sustainable.link ? (
                                        <a
                                            className="link"
                                            href={sustainable.link.url}
                                            onClick={() => {
                                                trackEvent((ampli, defaultProperties) => {
                                                    ampli.sustainablePageClicked({
                                                        ...defaultProperties,
                                                        block: 'sustainability',
                                                    });
                                                });
                                            }}
                                        >
                                            {sustainable.link.label}
                                        </a>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        {brandCommunications.length > 0 ? (
                            <div ref={newsBlockRef} className={style.newsBlock}>
                                <div className={style.newsHeader}>
                                    <h2 className={style.newsTitle}>
                                        {formatMessage({
                                            id: 'page.home.brandSection.actuTitle',
                                        })}
                                        <BrandHandMarkTwoLinesVariant3
                                            className={style.newsTitleHandMark}
                                        />
                                    </h2>

                                    <Typography
                                        as="div"
                                        className={style.newsSubtitle}
                                        scale="secondary-xs-bold"
                                    >
                                        {formatMessage({
                                            id: 'page.home.brandSection.actuSubtitle',
                                        })}
                                    </Typography>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {brandCommunications.length > 0 ? (
                <NaturalScrollCarousel
                    slidesGap="16px"
                    scrollSnapAlign="center"
                    trackingInformation={{
                        navClick: {
                            block: 'brand',
                        },
                    }}
                >
                    {brandCommunications.map((brandCommunication, index) => (
                        <NaturalScrollCarousel.Slide key={brandCommunication.title}>
                            <BrandCommunicationCard
                                brandCommunication={brandCommunication}
                                trackingInformation={{
                                    click: {
                                        block: 'brand',
                                        card_number: index + 1,
                                    },
                                }}
                            />
                        </NaturalScrollCarousel.Slide>
                    ))}
                </NaturalScrollCarousel>
            ) : null}
        </section>
    );
}
